import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

/**
 * Parse a score string like "12200 4" or "9900 8*"
 * Also handles plain numbers (e.g., 64600 -> 64.600)
 * Returns { score: 12.200, rank: 4, isTie: false }
 */
const normalizeScoreValue = (rawValue, maxExpected) => {
  if (rawValue === null || rawValue === undefined || Number.isNaN(rawValue)) return 0;

  if (rawValue <= maxExpected) return rawValue;

  const thousand = rawValue / 1000;
  if (thousand <= maxExpected) return thousand;

  const tenThousand = rawValue / 10000;
  if (tenThousand <= maxExpected) return tenThousand;

  const hundredThousand = rawValue / 100000;
  if (hundredThousand <= maxExpected) return hundredThousand;

  return thousand;
};

const rotateRight2 = (valueStr) => {
  const padded = valueStr.padStart(5, '0');
  return padded.slice(-2) + padded.slice(0, 3);
};

const rotateRight1 = (valueStr) => {
  const padded = valueStr.padStart(4, '0');
  return padded.slice(-1) + padded.slice(0, 3);
};

const tryDecodeRankPrefixRotate1 = (rawValue, maxExpected) => {
  const str = String(rawValue).trim();
  if (!/^\d+$/.test(str) || str.length < 5) return null;

  const rotatedScoreStr = str.slice(-4);
  const rankStr = str.slice(0, -4);
  if (!rankStr) return null;

  const originalScoreStr = rotateRight1(rotatedScoreStr);
  const score = parseInt(originalScoreStr, 10) / 1000;
  if (score > 0 && score <= maxExpected) {
    return { score, rank: parseInt(rankStr, 10), isTie: false };
  }
  return null;
};

const tryDecodeRotatedRankScore = (rawValue, maxExpected) => {
  const str = String(rawValue).trim();
  if (!str) return null;

  if (str.includes('T')) {
    const [rankStr, rotatedStr] = str.split('T');
    if (!rankStr || !rotatedStr) return null;
    if (!/^\d+$/.test(rankStr) || !/^\d+$/.test(rotatedStr)) return null;
    const originalScoreStr = rotateRight2(rotatedStr);
    const score = parseInt(originalScoreStr, 10) / 1000;
    if (score > 0 && score <= maxExpected) {
      return { score, rank: parseInt(rankStr, 10), isTie: true };
    }
    return null;
  }

  if (!/^\d+$/.test(str) || str.length < 6) return null;
  const rotatedStr = str.slice(-5);
  const rankStr = str.slice(0, -5);
  if (!rankStr) return null;
  const originalScoreStr = rotateRight2(rotatedStr);
  const score = parseInt(originalScoreStr, 10) / 1000;
  if (score > 0 && score <= maxExpected) {
    return { score, rank: parseInt(rankStr, 10), isTie: false };
  }
  return null;
};

const tryDecodeNoSpace = (rawValue, maxExpected) => {
  const num = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue);
  if (!num || Number.isNaN(num)) return null;

  const intVal = Math.floor(num);
  const candidates = [];

  // Try last 2 digits as rank
  const rank2 = intVal % 100;
  const score2a = (intVal - rank2) / 1000;
  const score2b = (intVal - rank2) / 10000;
  candidates.push({ score: score2a, rank: rank2, isTie: false });
  candidates.push({ score: score2b, rank: rank2, isTie: false });

  // Try last 1 digit as rank
  const rank1 = intVal % 10;
  const score1a = (intVal - rank1) / 1000;
  const score1b = (intVal - rank1) / 10000;
  candidates.push({ score: score1a, rank: rank1, isTie: false });
  candidates.push({ score: score1b, rank: rank1, isTie: false });

  const valid = candidates.filter(c => c.score > 0 && c.score <= maxExpected);
  if (valid.length === 0) return null;

  // Prefer higher precision (closer to maxExpected but not exceeding)
  valid.sort((a, b) => Math.abs(maxExpected - a.score) - Math.abs(maxExpected - b.score));
  return valid[0];
};

const parseScoreString = (scoreStr, maxExpected = 20) => {
  if (!scoreStr || scoreStr === '') {
    return { score: 0, rank: null, isTie: false };
  }

  // Handle plain numbers (when copying from website loses decimals)
  if (typeof scoreStr === 'number') {
    const rotated = tryDecodeRotatedRankScore(scoreStr, maxExpected);
    if (rotated) {
      return { score: rotated.score, rank: rotated.rank, isTie: rotated.isTie };
    }
    const rotate1 = tryDecodeRankPrefixRotate1(scoreStr, maxExpected);
    if (rotate1) {
      return { score: rotate1.score, rank: rotate1.rank, isTie: rotate1.isTie };
    }
    const decoded = tryDecodeNoSpace(scoreStr, maxExpected);
    if (decoded) {
      return { score: decoded.score, rank: decoded.rank, isTie: decoded.isTie };
    }
    const scoreValue = normalizeScoreValue(scoreStr, maxExpected);
    return { score: scoreValue, rank: null, isTie: false };
  }

  const str = String(scoreStr).trim();
  const rotated = tryDecodeRotatedRankScore(str, maxExpected);
  if (rotated) {
    return { score: rotated.score, rank: rotated.rank, isTie: rotated.isTie };
  }
  const rotate1 = tryDecodeRankPrefixRotate1(str, maxExpected);
  if (rotate1) {
    return { score: rotate1.score, rank: rotate1.rank, isTie: rotate1.isTie };
  }
  if (/^\d+$/.test(str) && str.length >= 4) {
    const decoded = tryDecodeNoSpace(str, maxExpected);
    if (decoded) {
      return { score: decoded.score, rank: decoded.rank, isTie: decoded.isTie };
    }
  }
  const parts = str.split(' ');

  if (parts.length < 2) {
    // Try to parse as a plain number string
    const num = parseFloat(str);
    if (!isNaN(num)) {
      const scoreValue = normalizeScoreValue(num, maxExpected);
      return { score: scoreValue, rank: null, isTie: false };
    }
    return { score: 0, rank: null, isTie: false };
  }

  // Parse score with rank (e.g., "12700 2" -> score: 12.700, rank: 2)
  const rawScore = parseFloat(parts[0]);
  const scoreValue = normalizeScoreValue(rawScore, maxExpected);
  // Parse rank (e.g., "4*" -> rank: 4, isTie: true)
  const rankStr = parts[1];
  const isTie = rankStr.includes('*') || rankStr.includes('T');
  const rank = parseInt(rankStr.replace(/[*T]/g, ''));

  return {
    score: isNaN(scoreValue) ? 0 : scoreValue,
    rank: isNaN(rank) ? null : rank,
    isTie
  };
};

/**
 * Parse gymnast data from Excel row
 */
const parseGymnastRow = (row) => {
  const gymnast = {
    name: row[0] || '',
    team: row[1] || '',
    session: row[2] || 0,      // Session number (1, 2, 3)
    level: row[3] || '',        // Level (7, 8, 9, 10, 3D1, 4D1, etc.)
    division: row[4] || '',     // Division (Group 1, Group 2, etc.)
    floor: parseScoreString(row[5], 20),
    pommelHorse: parseScoreString(row[6], 20),
    rings: parseScoreString(row[7], 20),
    vault: parseScoreString(row[8], 20),
    parallelBars: parseScoreString(row[9], 20),
    highBar: parseScoreString(row[10], 20),
    allAround: parseScoreString(row[11], 80)
  };

  return gymnast;
};

/**
 * Group gymnasts by level and division
 */
const groupByLevelAndDivision = (gymnasts) => {
  const groups = {};

  gymnasts.forEach(gymnast => {
    const key = `Level${gymnast.level}-${gymnast.division}`;

    if (!groups[key]) {
      groups[key] = {
        level: gymnast.level,
        division: gymnast.division,
        athletes: []
      };
    }

    groups[key].athletes.push(gymnast);
  });

  return groups;
};

/**
 * Get top 10 all-stars from a group of gymnasts
 */
const getTop10 = (gymnasts) => {
  // Sort by all-around score (descending)
  const sorted = [...gymnasts].sort((a, b) => {
    return b.allAround.score - a.allAround.score;
  });

  // Take top 10
  return sorted.slice(0, 10);
};

const normalizeHeader = (value) => String(value || '')
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, '');

const extractCellText = (cell) => String(cell?.textContent || '')
  .replace(/\s+/g, ' ')
  .trim();

const parseMeetRowsFromHtml = (htmlText) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, 'text/html');
  const tables = Array.from(doc.querySelectorAll('table'));

  for (const table of tables) {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length < 2) continue;

    const headerRow = rows.find((row) => row.querySelectorAll('th').length >= 8);
    if (!headerRow) continue;

    const headerCells = Array.from(headerRow.querySelectorAll('th,td'));
    const normalizedHeaders = headerCells.map((cell) => normalizeHeader(extractCellText(cell)));

    const requiredHeaders = ['GYMNAST', 'TEAM', 'FLOOR', 'POMML', 'RINGS', 'VAULT', 'PBARS', 'HIBAR', 'AA'];
    const hasRequiredHeaders = requiredHeaders.every((key) => normalizedHeaders.includes(key));
    if (!hasRequiredHeaders) continue;

    const headerIndex = {};
    normalizedHeaders.forEach((header, index) => {
      if (!headerIndex[header]) {
        headerIndex[header] = index;
      }
    });

    const dataRows = [];
    rows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length < headerCells.length) return;

      const rowValues = cells.map((cell) => extractCellText(cell));
      const gymnastName = rowValues[headerIndex.GYMNAST] || '';
      const aaValue = rowValues[headerIndex.AA] || '';
      if (!gymnastName || !aaValue) return;

      dataRows.push([
        gymnastName,
        rowValues[headerIndex.TEAM] || '',
        rowValues[headerIndex.SESS] || '',
        rowValues[headerIndex.LVL] || '',
        rowValues[headerIndex.DIV] || '',
        rowValues[headerIndex.FLOOR] || '',
        rowValues[headerIndex.POMML] || '',
        rowValues[headerIndex.RINGS] || '',
        rowValues[headerIndex.VAULT] || '',
        rowValues[headerIndex.PBARS] || '',
        rowValues[headerIndex.HIBAR] || '',
        aaValue
      ]);
    });

    if (dataRows.length > 0) {
      return dataRows;
    }
  }

  return [];
};

const parseMeetRowsFromProxyText = (rawText) => {
  const lines = String(rawText || '')
    .split('\n')
    .map((line) => line.replace(/\r/g, '').trim())
    .filter(Boolean);

  const rows = [];
  const scoreWithRankRegex = /(\d+\.\d+)\s+(\d+\*?T?)/g;

  lines.forEach((line) => {
    // First try tab-delimited proxy lines.
    const tabParts = line.split('\t').map((part) => part.trim()).filter(Boolean);
    if (tabParts.length >= 12 && /\d+\.\d+/.test(tabParts[5])) {
      rows.push(tabParts.slice(0, 12));
      return;
    }

    // Then parse generic flattened text lines by locating the 7 trailing score/rank cells.
    const scoreMatches = Array.from(line.matchAll(scoreWithRankRegex));
    if (scoreMatches.length < 7) return;

    const scoreCells = scoreMatches.slice(-7).map((m) => `${m[1]} ${m[2]}`);
    const firstScoreIndex = scoreMatches[scoreMatches.length - 7]?.index ?? -1;
    if (firstScoreIndex < 0) return;

    const prefix = line.slice(0, firstScoreIndex).trim();
    if (!prefix) return;

    // Prefer stable multi-space column breaks if present.
    const prefixParts = prefix.split(/\s{2,}/).map((p) => p.trim()).filter(Boolean);
    if (prefixParts.length >= 5) {
      rows.push([
        prefixParts[0],
        prefixParts[1],
        prefixParts[2],
        prefixParts[3],
        prefixParts.slice(4).join(' '),
        ...scoreCells
      ]);
      return;
    }

    // Fallback: split from the right for session/level/div.
    const tokens = prefix.split(/\s+/).filter(Boolean);
    if (tokens.length < 5) return;
    const division = tokens.pop();
    const level = tokens.pop();
    const session = tokens.pop();

    // Name/team split is ambiguous in single-space text. Use first two tokens for name.
    const name = tokens.slice(0, 2).join(' ').trim();
    const team = tokens.slice(2).join(' ').trim();
    if (!name || !team) return;

    rows.push([name, team, session, level, division, ...scoreCells]);
  });

  return rows;
};

const buildMeetResults = (rows) => {
  const gymnasts = [];

  rows.forEach((row) => {
    const gymnast = parseGymnastRow(row);
    if (gymnast.allAround.score > 0) {
      gymnasts.push(gymnast);
    }
  });

  const groups = groupByLevelAndDivision(gymnasts);
  const allStars = {};
  Object.keys(groups).forEach((key) => {
    const group = groups[key];
    allStars[key] = {
      level: group.level,
      division: group.division,
      top10: getTop10(group.athletes),
      totalAthletes: group.athletes.length
    };
  });

  return {
    totalGymnasts: gymnasts.length,
    groups: allStars,
    allGymnasts: gymnasts
  };
};

const normalizePdfLine = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const buildPdfPageLines = async (pdfPage) => {
  const textContent = await pdfPage.getTextContent();
  const rows = [];
  const yTolerance = 1.6;

  textContent.items.forEach((item) => {
    const str = normalizePdfLine(item.str);
    if (!str) return;

    const x = item.transform[4];
    const y = item.transform[5];

    let row = rows.find((entry) => Math.abs(entry.y - y) <= yTolerance);
    if (!row) {
      row = { y, tokens: [] };
      rows.push(row);
    }
    row.tokens.push({ x, str });
  });

  rows.sort((a, b) => b.y - a.y);

  return rows
    .map((row) => row.tokens
      .sort((a, b) => a.x - b.x)
      .map((token) => token.str)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim())
    .filter(Boolean);
};

const parseRowsFromPdfLines = (lines) => {
  const rows = [];
  let session = '';
  let level = '';
  let division = '';
  const athleteLineRegex = /^\s*\d+\*?T?\s+\d+\s+(.+?)\s+([0-9]+(?:\.[0-9]+)?)\s+([0-9]+(?:\.[0-9]+)?)\s+([0-9]+(?:\.[0-9]+)?)\s+([0-9]+(?:\.[0-9]+)?)\s+([0-9]+(?:\.[0-9]+)?)\s+([0-9]+(?:\.[0-9]+)?)\s+([0-9]+(?:\.[0-9]+)?)\s*$/;
  const likelySurnameWithRanksRegex = /^([A-Za-z][A-Za-z'.-]+)\s+(?:\d+\*?T?\s+){5,7}\d+\*?T?$/;
  const textThenRanksRegex = /^([A-Za-z0-9'&.\-/ ]+?)\s+(?:\d+\*?T?\s+){5,7}\d+\*?T?$/;
  const sanitizeTeamText = (value) => normalizePdfLine(value)
    .split(' ')
    .filter((token) => !/^\d+\*?T?$/.test(token))
    .join(' ')
    .trim();

  for (let i = 0; i < lines.length; i += 1) {
    const line = normalizePdfLine(lines[i]);
    if (!line) continue;

    const sessionMatch = line.match(/Session:\s*([A-Za-z0-9]+)/i);
    if (sessionMatch) session = sessionMatch[1].trim();

    const levelMatch = line.match(/Level:\s*([A-Za-z0-9+]+)/i);
    if (levelMatch) level = levelMatch[1].trim();

    const divisionMatch = line.match(/Division:\s*(.+)$/i);
    if (divisionMatch) division = divisionMatch[1].trim();

    const athleteMatch = line.match(athleteLineRegex);
    if (!athleteMatch) continue;

    const [, rawName, floor, pommel, rings, vault, pbars, hibar, aa] = athleteMatch;
    let name = rawName.trim();
    let teamSearchStart = i + 1;

    // Some PDFs wrap the last name into the next line before rank values.
    const nextLine = normalizePdfLine(lines[i + 1] || '');
    const lineAfterNext = normalizePdfLine(lines[i + 2] || '');
    if (
      name.split(' ').length === 1 &&
      likelySurnameWithRanksRegex.test(nextLine) &&
      lineAfterNext &&
      !/\d/.test(lineAfterNext) &&
      !/^(Session|Level|Division|Official Results)/i.test(lineAfterNext)
    ) {
      name = `${name} ${nextLine.split(/\s+/)[0]}`.trim();
      teamSearchStart = i + 2;
    }

    let team = '';
    for (let j = teamSearchStart; j < Math.min(i + 6, lines.length); j += 1) {
      const candidate = normalizePdfLine(lines[j]);
      if (!candidate) continue;
      if (/^Official Results/i.test(candidate)) break;
      if (/^\d+\s+\d+/.test(candidate)) break;
      if (/^(Session|Level|Division):/i.test(candidate)) break;

      const gymMatch = candidate.match(textThenRanksRegex) || candidate.match(/^([A-Za-z0-9'&.\-/ ]+?)$/);
      if (gymMatch && gymMatch[1]) {
        const textValue = sanitizeTeamText(gymMatch[1]);
        if (textValue && !/^Gym$/i.test(textValue)) {
          // Join wrapped team names like "Flagstaff" + "Gymnastics".
          const nextCandidate = sanitizeTeamText(lines[j + 1] || '');
          if (nextCandidate && /^[A-Za-z'&.\-/ ]+$/.test(nextCandidate) && !/^(Session|Level|Division|Official Results)/i.test(nextCandidate)) {
            team = `${textValue} ${nextCandidate}`.replace(/\s+/g, ' ').trim();
          } else {
            team = textValue;
          }
          break;
        }
      }
    }

    rows.push([
      name,
      team,
      session,
      level,
      division,
      floor,
      pommel,
      rings,
      vault,
      pbars,
      hibar,
      aa
    ]);
  }

  return rows;
};

/**
 * Main parser function - reads Excel file and returns top 10 all-stars by level/age
 */
export const parseMeetResults = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to array of arrays
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

        const dataRows = rows.slice(1).filter((row) => row[0]);
        resolve(buildMeetResults(dataRows));

      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};

export const parseMeetResultsFromUrl = async (meetUrl) => {
  const url = String(meetUrl || '').trim();
  if (!url) {
    throw new Error('A meet URL is required.');
  }

  const normalizedUrl = url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`;

  const noSchemeUrl = normalizedUrl.replace(/^https?:\/\//, '');
  const fetchTargets = [
    `https://r.jina.ai/http://${noSchemeUrl}`,
    `https://api.allorigins.win/raw?url=${encodeURIComponent(normalizedUrl)}`,
    `https://corsproxy.io/?${encodeURIComponent(normalizedUrl)}`
  ];

  let lastError = null;
  let gotResponseButNoRows = false;
  for (const target of fetchTargets) {
    try {
      const response = await fetch(target);
      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      const responseText = await response.text();
      let rows = parseMeetRowsFromHtml(responseText);
      if (rows.length === 0) {
        rows = parseMeetRowsFromProxyText(responseText);
      }

      if (rows.length > 0) {
        return buildMeetResults(rows);
      }
      gotResponseButNoRows = true;
    } catch (error) {
      lastError = error;
    }
  }

  if (gotResponseButNoRows) {
    throw new Error('Could not find a supported results table on that page.');
  }

  throw new Error(lastError?.message || 'Could not scrape meet data from URL.');
};

export const parseMeetResultsFromPdf = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdfData = new Uint8Array(arrayBuffer);

  const loadingTask = pdfjsLib.getDocument({ data: pdfData });
  const pdf = await loadingTask.promise;
  const allLines = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const pageLines = await buildPdfPageLines(page);
    allLines.push(...pageLines);
  }

  const rows = parseRowsFromPdfLines(allLines);
  if (rows.length === 0) {
    throw new Error('No athlete rows found in PDF.');
  }

  return buildMeetResults(rows);
};

/**
 * Format score for display (e.g., 71.800)
 */
export const formatScore = (score) => {
  return score.toFixed(3);
};

/**
 * Get level name from number
 */
export const getLevelName = (level) => {
  return `Level ${level}`;
};

/**
 * Get readable group key (e.g., "Level 8 - Group 1")
 */
export const getGroupLabel = (level, division) => {
  return `Level ${level} - ${division}`;
};

/**
 * Sort group keys for display (Level 3D1, 4D1, 5D1, 6D1, 7, 8, 9, 10)
 */
export const sortGroupKeys = (groupsObj) => {
  return Object.keys(groupsObj).sort((a, b) => {
    const aGroup = groupsObj[a];
    const bGroup = groupsObj[b];

    // Extract numeric part for comparison
    const aLevel = String(aGroup.level);
    const bLevel = String(bGroup.level);

    // Handle D1 levels (3D1, 4D1, etc.)
    const aIsD1 = aLevel.includes('D1');
    const bIsD1 = bLevel.includes('D1');

    if (aIsD1 && bIsD1) {
      // Both are D1 levels, compare numerically
      const aNum = parseInt(aLevel);
      const bNum = parseInt(bLevel);
      if (aNum !== bNum) return aNum - bNum;
    } else if (aIsD1 && !bIsD1) {
      // D1 levels come first
      return -1;
    } else if (!aIsD1 && bIsD1) {
      // Regular levels come after D1
      return 1;
    } else {
      // Both are regular levels, compare numerically
      const aNum = parseInt(aLevel);
      const bNum = parseInt(bLevel);
      if (aNum !== bNum) return aNum - bNum;
    }

    // If levels are equal, sort by division
    return String(aGroup.division || '').localeCompare(String(bGroup.division || ''));
  });
};
